// #region Imports and Dependencies
import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';

import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DirectionService } from 'src/app/core/services/direction.service';
import { take, Subject, takeUntil, distinctUntilChanged, combineLatest, debounceTime, switchMap, of, catchError, startWith, share, filter, BehaviorSubject, merge } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatStepperModule } from '@angular/material/stepper';
import { FonctionnelService } from 'src/app/core/services/fonctionnel.service';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { Poste } from '@models/domains/posts';
import { AgentsService } from 'src/app/core/services/agents.service';
import { Agent } from '@models/domains/administration';
import { Pole } from '@models/shared';
import { TypeRecrutement } from '@models/domains/recruitment';
import { MotifLiberation } from '@models/domains/recruitment';
import { RecruitmentUtils } from 'src/app/shared/utils/recruitment.utils';
// #endregion

// #region Types and Utilities
// Function to compare two Poste objects for distinctUntilChanged
export function comparePostes(prev: Poste | null, curr: Poste | null): boolean {
  // If both are null or undefined, they're equal
  if (!prev && !curr) return true;
  // If only one is null/undefined, they're different
  if (!prev || !curr) return false;
  // Compare by ID
  return prev.id === curr.id;
}
// #endregion

// #region Component Configuration
@Component({
    selector: 'app-informations-generales-form',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatStepperModule
],
    templateUrl: './informations-generales-form.component.html',
    styleUrls: ['./informations-generales-form.component.scss']
})
// #endregion

// #region Component Class and Properties
export class InformationsGeneralesFormComponent implements OnDestroy {
  // Make Validators available in template
  public ValidatorsRef = Validators;
  // #region Input/Output Properties
  @Input() form!: FormGroup;
  @Output() formReset = new EventEmitter<void>();
  @Output() posteDetailsChange = new EventEmitter<Poste | undefined>();
  // #endregion

  // #region Data Lists and Filtered Lists
  serviceFonctionnelList: any[] = [];
  serviceFonctionnelListFiltered: any[] = [];
  recrutementList: TypeRecrutement[] = [];
  recrutementListFiltered: TypeRecrutement[] = [];
  poleList: Pole[] = [];
  poleListFiltered: Pole[] = [];
  directionList: any[] = [];
  directionListFiltered: any[] = [];
  posteList: Poste[] = [];
  posteListFiltered: Poste[] = [];
  numeroPosteListFiltered: Poste[] = [];
  posteLoadError: string | null = null; // Error message for post loading failures
  motifLiberationListFiltered: MotifLiberation[] = [];
  motifLiberationList: MotifLiberation[] = [];
  // #endregion

  // #region Component State
  private readonly destroy$ = new Subject<void>();
  isLoading = false;
  isLoadingPostes = false;
  
  // #endregion

  // #region Constructor and Dependency Injection
  constructor(
    private readonly agentsService: AgentsService,
    private readonly directionService: DirectionService,
    private readonly fonctionnelService: FonctionnelService,
    private readonly referentielsService: ReferentielsService
  ) {}
  // #endregion

  // #region Lifecycle Hooks
  ngOnInit() {
    this.loadListTypeRecrutement();
    this.loadListPole();
    this.initUserDetails();
    this.setupReactiveFormStream();
    this.loadListMotifLiberation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  // #endregion

  // #region Initialization and Setup
  private initUserDetails() {
    this.agentsService
      .getCurrentAgent()
      .pipe(takeUntil(this.destroy$))
      .subscribe((agent: Agent) => {
        if (agent) {
          this.initChampsSuivantAgent(agent);
        }
      });
  }

  // Simplified initialization - just set form values, let reactive stream handle loading
  private initChampsSuivantAgent(agent: Agent) {
    const pole = agent?.direction?.pole;
    const direction = agent?.direction;
    const service = agent?.serviceFonctionnel;

    // Pre-populate data arrays to avoid unnecessary API calls
    if (direction) {
      this.directionList = [direction];
      this.directionListFiltered = [direction];
    }

    if (service) {
      this.serviceFonctionnelList = [service];
      this.serviceFonctionnelListFiltered = [service];
    }

    // Set form values - this will trigger the reactive stream
    this.form.patchValue({
      pole: pole,
      direction: direction,
      service: service
    });
  }
  // #endregion

  // #region Reactive Form Stream Management
  private setupReactiveFormStream() {
    // Single reactive stream that handles all form state changes
    const poleChanges$ = this.form.get('pole')?.valueChanges || of(null);
    const directionChanges$ = this.form.get('direction')?.valueChanges || of(null);
    const serviceChanges$ = this.form.get('service')?.valueChanges || of(null);

    // Reset service field when direction changes
    directionChanges$
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        takeUntil(this.destroy$)
      )
      .subscribe((direction) => {
        // Reset service field when direction changes to ensure service belongs to new direction
        const currentService = this.form.get('service')?.value;
        if (currentService && direction) {
          this.form.get('service')?.setValue(null, { emitEvent: false });
        }
        
        // Immediately clear post lists to prevent invalid selections
        this.clearPosteList();
        this.isLoadingPostes = true;
      });

    // Reset poste fields when service or direction changes
    combineLatest([directionChanges$, serviceChanges$])
      .pipe(
        distinctUntilChanged(([prevDir, prevServ], [currDir, currServ]) => {
          return prevDir?.id === currDir?.id && prevServ?.id === currServ?.id;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([direction, service]) => {
        // Reset poste fields when direction or service changes
        const currentIntitule = this.form.get('intituleposte')?.value;
        const currentNumero = this.form.get('numeroPoste')?.value;
        
        if ((currentIntitule || currentNumero) && (direction || service)) {
          this.form.get('intituleposte')?.setValue(null, { emitEvent: false });
          this.form.get('numeroPoste')?.setValue(null, { emitEvent: false });
        }
        
        // Immediately clear post lists to prevent invalid selections
        this.clearPosteList();
        this.isLoadingPostes = true;
      });

    combineLatest([poleChanges$, directionChanges$, serviceChanges$])
      .pipe(
        debounceTime(50), // Prevent race conditions
        distinctUntilChanged(([prevPole, prevDir, prevServ], [currPole, currDir, currServ]) => {
          return prevPole?.id === currPole?.id && 
                 prevDir?.id === currDir?.id && 
                 prevServ?.id === currServ?.id;
        }),
        switchMap(([pole, direction, service]) => {
          this.isLoading = true;
          return this.loadDataBasedOnState(pole, direction, service);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          // Update field states after loading
          this.updateFieldStates();
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error in reactive form stream:', error);
        }
      });

    // Single coordinated stream for position field synchronization
    this.setupPositionFieldStream();
  }

  // #region Position Field Stream Management
  private setupPositionFieldStream(): void {
    const intitule$ = this.form.get('intituleposte')!.valueChanges;
    const numero$ = this.form.get('numeroPoste')!.valueChanges;

    merge(intitule$, numero$)
      .pipe(
        // We only want to synchronize when a `Poste` object is selected from the autocomplete,
        // not when the user is typing (which emits a string).
        // `null` is also allowed to pass through, for when the field is cleared.
        filter((value): value is Poste | null => typeof value !== 'string'),
        debounceTime(0), // Synchronize fields in the same tick
        distinctUntilChanged(comparePostes),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedPoste: Poste | null) => {
        this.updatePositionFields(selectedPoste);
      });
  }

  private updatePositionFields(poste: Poste | null): void {
    // If a poste is selected, ensure both fields are synchronized
    if (poste) {
      this.form.get('intituleposte')?.setValue(poste, { emitEvent: false });
      this.form.get('numeroPoste')?.setValue(poste, { emitEvent: false });
      this.posteDetailsChange.emit(poste);
    } else {
      // If cleared, ensure both fields are null
      this.form.get('intituleposte')?.setValue(null, { emitEvent: false });
      this.form.get('numeroPoste')?.setValue(null, { emitEvent: false });
      this.posteDetailsChange.emit(undefined);
    }
  }
  // #endregion

  // #region Form State Management
  // Declarative field state management
  private updateFieldStates() {
    // Direction field state
    if (this.directionList.length <= 1) {
      this.form.get('direction')?.disable();
    } else {
      this.form.get('direction')?.enable();
    }

    // Service field is always enabled (unless form is disabled)
    if (!this.form.disabled) {
      this.form.get('service')?.enable();
    }

    // Poste fields state
    const hasDirection = this.form.get('direction')?.value;
    const isVacataire = this.isVacataireType();

    if (isVacataire) {
      this.disablePosteFields();
    } else if (hasDirection) {
      this.enablePosteFields();
    } else {
      this.disablePosteFields();
    }
  }

  // Method to enable poste fields
  private enablePosteFields() {
    if (this.isVacataireType()) {
      this.disablePosteFields();
      return;
    }
    this.form.get('intituleposte')?.enable();
    this.form.get('numeroPoste')?.enable();
  }

  // Method to disable poste fields
  private disablePosteFields() {
    this.form.get('intituleposte')?.disable();
    this.form.get('numeroPoste')?.disable();
  }

  // Handle auto-selection for single direction
  private handleDirectionAutoSelection(): void {
    // No directions available - disable field and clear value
    if (this.directionList.length === 0) {
      this.form.get('direction')?.setValue(null);
      return;
    }

    // Single direction - auto-select
    if (this.directionList.length === 1) {
      this.form.get('direction')?.setValue(this.directionList[0], { emitEvent: false });
      return;
    }

    // Multiple directions - no auto-selection needed
  }
  // #endregion

  // #region User Interaction Handlers

  onReset() {
    // Only reset enabled fields
    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.get(controlName);
      if (control?.enabled) {
        control.reset();
      }
    });

    this.formReset.emit();
  }

  // Clear functions - let reactive stream handle all loading
  clearField(event: Event, controlName: string) {
    event.stopPropagation();
    this.form.get(controlName)?.setValue(null);

    // Handle direction clearing
    if (controlName === 'direction') {
      this.directionListFiltered = [...this.directionList];
      // Reset dependent fields - reactive stream will handle loading
      this.form.get('service')?.setValue(null);
      this.form.get('intituleposte')?.setValue(null);
      this.form.get('numeroPoste')?.setValue(null);
      return;
    }

    // Handle service clearing - clear poste fields and let reactive stream reload based on direction only
    if (controlName === 'service') {
      this.form.get('intituleposte')?.setValue(null);
      this.form.get('numeroPoste')?.setValue(null);
      return;
    }
  }
  // #endregion

  // #region Autocomplete Display Functions
  typeRecrutementdisplayFn(item?: any): string {
    return item ? item.libelle : '';
  }

  poledisplayFn(item?: any): string {
    return item ? item.libelle : '';
  }

  directiondisplayFn(item?: any): string {
    return item ? item.libelle : '';
  }

  servicedisplayFn(item?: any): string {
    return item ? item.libelle : '';
  }

  IntitulePostedisplayFn(item?: any): string {
    return item ? `${item.libelle} ${item.code ?? ''}` : '';
  }

  numeroPosteDisplayFn(item?: any): string {
    return item ? item.numero : '';
  }
  // #endregion

  // #region Autocomplete Filter Functions
  filterRecrutementList(event: any): void {
    const value = event.target.value?.toLowerCase() ?? '';
    this.recrutementListFiltered = this.recrutementList.filter(
      (type) => typeof value === 'string' && type.libelle.toLowerCase().includes(value)
    );
  }

  directionFilter(event: any): void {
    const value = event.target.value?.toLowerCase() ?? '';
    this.directionListFiltered = this.directionList.filter(
      (direction) => typeof value === 'string' && direction.libelle.toLowerCase().includes(value)
    );
  }

  serviceFilter(event: any): void {
    const value = event.target.value?.toLowerCase() ?? '';
    this.serviceFonctionnelListFiltered = this.serviceFonctionnelList.filter(
      (service) => typeof value === 'string' && service.libelle.toLowerCase().includes(value)
    );
  }

  IntitulePosteFilter(event: any): void {
    const value = event.target.value?.toLowerCase() ?? '';
    this.posteListFiltered = this.posteList.filter((poste) => typeof value === 'string' && poste.libelle.toLowerCase().includes(value));
  }

  numeroPosteFilter(event: any): void {
    const value = event.target.value?.toLowerCase() ?? '';
    this.numeroPosteListFiltered = this.posteList.filter(
      (poste) => typeof value === 'string' && poste.numero?.toString().toLowerCase().includes(value)
    );
  }
  // #endregion

  // #region Data Loading - Core Methods
  private loadListTypeRecrutement() {
    this.referentielsService
      .getTypeRecrutement()
      .pipe(take(1))
      .subscribe((data: TypeRecrutement[]) => {
        if (data) {
          this.recrutementList = data.filter((o) => o.libelle && o.libelle.trim() !== '');
          this.recrutementListFiltered = [...this.recrutementList];
        }
      });
  }

  private loadListPole() {
    this.referentielsService
      .getPole()
      .pipe(take(1))
      .subscribe((data: Pole[]) => {
        if (data) {
          this.poleList = data.filter((o) => o.libelle && o.libelle.trim() !== '');
          this.poleListFiltered = [...this.poleList];
        }
      });
  }
  // #endregion

  // #region Data Loading - Reactive Streams
  // Centralized loading method that determines what to load based on current form state
  private loadDataBasedOnState(pole: any, direction: any, service: any) {
    return of(null).pipe(
      switchMap(() => {
        // Load directions if we have pole (always load to show all options)
        if (pole?.id) {
          return this.directionService.getMyDirectionsByPole(pole.id).pipe(
            take(1),
            switchMap((directions) => {
              this.handleDirectionsLoaded(directions);
              return this.loadServicesAndPostes(direction, service);
            })
          );
        }
        
        // Load services and postes based on current state
        return this.loadServicesAndPostes(direction, service);
      })
    );
  }

  private loadServicesAndPostes(direction: any, service: any) {
    return of(null).pipe(
      switchMap(() => {
        // Load services if we have direction to populate the autocomplete options
        if (direction?.id) {
          return this.fonctionnelService.getMyServicesByDirection(direction.id).pipe(
            take(1),
            switchMap((services) => {
              this.handleServicesLoaded(services);
              return this.loadPostes(direction, service);
            })
          );
        }
        
        // Load postes based on current state
        return this.loadPostes(direction, service);
      })
    );
  }

  private loadPostes(direction: any, service: any) {
    return of(null).pipe(
      switchMap(() => {
        const directionId = direction?.id;
        const serviceId = service?.id;
        
        if (!directionId && !serviceId) {
          this.clearPosteList();
          this.isLoadingPostes = false;
          return of(null);
        }
        
        return this.referentielsService.getAllPostesOpened(directionId, serviceId).pipe(
          take(1),
          switchMap((postes) => {
            this.posteList = postes;
            this.posteListFiltered = [...this.posteList];
            this.numeroPosteListFiltered = [...this.posteList];
            // Clear any previous error when posts load successfully
            this.posteLoadError = null;
            this.isLoadingPostes = false;
            return of(null);
          }),
          catchError((error) => {
            this.clearPosteList();
            // Set error message for display in template instead of showing modal
            this.posteLoadError = error.error || 'Erreur lors du chargement des postes';
            this.isLoadingPostes = false;
            return of(null);
          })
        );
      })
    );
  }
  // #endregion

  // #region Data Processing Helpers
  private handleDirectionsLoaded(directions: any[]) {
    if (directions) {
      this.directionList = directions.filter((o: any) => o.libelle && o.libelle.trim() !== '');
      this.directionListFiltered = [...this.directionList];
    } else {
      this.directionList = [];
      this.directionListFiltered = [];
    }
    this.handleDirectionAutoSelection();
  }

  private handleServicesLoaded(services: any[]) {
    if (services) {
      this.serviceFonctionnelList = services.filter((service: any) => service && service.libelle);
      this.serviceFonctionnelListFiltered = [...this.serviceFonctionnelList];
    } else {
      this.serviceFonctionnelList = [];
      this.serviceFonctionnelListFiltered = [];
    }
  }

  private clearPosteList() {
    this.posteList = [];
    this.posteListFiltered = [];
    this.numeroPosteListFiltered = [];
    // Clear error when list is intentionally cleared (e.g., direction/service change)
    this.posteLoadError = null;
  }
  // #endregion

  // #region Motif Liberation Management
  motifLiberationFilter(event: any): void {
    const value = event.target.value;
    if (typeof value !== 'string') {
      this.motifLiberationList = [];
      return;
    }
    const filterValue = value.toLowerCase().split(' ').join('');
    this.motifLiberationListFiltered = this.motifLiberationList.filter((item: any) => item.libelle.toLowerCase().includes(filterValue));
  }

  motifLiberationdisplayFn(motifLiberation?: any): string {
    return motifLiberation ? motifLiberation.libelle : '';
  }

  effaceFilteMotifLiberation(event: any, controlName: string) {
    event.stopPropagation();
    if (controlName != undefined && controlName != '') {
      this.form.controls[controlName].setValue('');
      this.loadListMotifLiberation();
    }
  }

  loadListMotifLiberation() {
    this.referentielsService
      .getMotifDepart()
      .pipe(take(1))
      .subscribe((data: MotifLiberation[]) => {
        if (data) {
          this.motifLiberationList = data.filter((o) => o.libelle && o.libelle.trim() !== '');
          this.motifLiberationListFiltered = [...this.motifLiberationList];
        }
      });
  }

  // Check if current recruitment type is vacataire (ACR, SAIS, APP)
  isVacataireType(): boolean {
    const typeRecrutement = this.form.get('typeRecrutement')?.value;
    return RecruitmentUtils.isVacataireType(typeRecrutement);
  }
  // #endregion
}
// #endregion
